import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import Button from '../../components/ui/Button';
import { formatDate, extractListData } from '../../utils/Utils';

const Wrap = styled.div`
  margin-top: 20px;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 10px;
  border-bottom: 1px solid #E8ECF0;
  padding-bottom: 6px;
`;

const EntryRow = styled.div`
  padding: 10px 12px;
  background: #F5F7FA;
  border-radius: 6px;
  margin-bottom: 8px;
  font-size: 13px;
`;

const EntryHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  flex-wrap: wrap;
`;

const EntryKind = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EntryChange = styled.span`
  color: ${({ theme }) => theme.colors.text.primary};
`;

const EntryMeta = styled.div`
  margin-top: 3px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 12px;
  color: #6c757d;
`;

const EntryNote = styled.div`
  margin-top: 5px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.primary};
  white-space: pre-wrap;
`;

const EmptyBox = styled.div`
  padding: 14px;
  text-align: center;
  color: #6c757d;
  font-size: 13px;
`;

const PagerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-top: 10px;
`;

const PageLabel = styled.span`
  font-size: 12px;
  color: #6c757d;
`;

const PAGE_SIZE = 10;

// Format a check-in value per its kind — kr_progress is a 0-100 percentage
// (mirrors GoalEditor's `{kr.progressPct}%` rendering), kpi_actual is a raw
// number (mirrors `{goal.kpiActualValue}` rendering elsewhere — DTO carries
// no unit for check-in rows, task-1-report.md §1).
// P3 (2026-10-08) — kilometre taşı ilerlemesi de yüzde (kabul edilen adım ağırlığı oranı); ikili
// eşik sonucu 1/0 olarak saklanır ve %100/%0 gösterilir (koşul sağlandı/sağlanmadı).
const PERCENT_KINDS = ['kr_progress', 'milestone_progress', 'milestone_step_submitted'];
const formatValue = (value, valueKind) => {
  if (value == null) return '—';
  if (valueKind === 'threshold_met') return Number(value) >= 1 ? '100%' : '0%';
  return PERCENT_KINDS.includes(valueKind) ? `${value}%` : `${value}`;
};

// Skorlama Faz F, Task 5 (spec §4, task-1-report.md §1/§3) — "İlerleme
// Geçmişi": GET .../check-ins (sayfalı, CreateDate DESC — en yeni önce).
// `apiPath` çağıranın hangi ucu kullanacağını belirler:
//   sahip   → /v1/period-goals/my/{id}/check-ins  (GetMyCheckInsAsync)
//   ekip/İK → /v1/period-goals/{id}/check-ins      (GetCheckInsAsync — yalnız
//             doğrudan yönetici/İK, task-1-report.md §3 "self-ban" DAHİL)
// İkinci uç, hedefi PM.Read (geniş) ile görebilen ama doğrudan yönetici/İK
// OLMAYAN bir görüntüleyicide 403 dönebilir (task-1-report.md concern #2 —
// GetByIdAsync'in kapsamı GetCheckInsAsync'ten GENİŞ: kalibrasyon bağlamında
// hedefin kendisi görülebilirken geçmişi görülemeyebilir). Bu component
// 403'ü (veya başka bir hata) toast'sız SESSİZCE yutar ve bölümü HİÇ
// RENDER ETMEZ (`status==='hidden'`) — yetkisiz bir "izniniz yok" kutusu
// göstermek yerine, bölüm o görüntüleyici için hiç var olmamış gibi
// davranır (brief: "toast yok" + sahip-dışı görünümde 403'ü ele al).
//
// Fix round 1 (final review F1) — `services/api.js`'in response interceptor'ı
// GET isteklerinde ASLA reject ETMEZ (mutasyon DEĞİLse her hata — 403 dahil —
// `Promise.resolve(EMPTY_RESPONSE)` ile döner, `EMPTY_RESPONSE.data.status`
// HER ZAMAN `false`; repo'nun belgeli tuzağı, `reference_api_interceptor_
// never_rejects_reads`). Yani aşağıdaki `catch` bloğu 403'te HİÇ ÇALIŞMAZ —
// gerçek sinyal resolve edilen gövdenin `data.status`'u. Bu yüzden başarılı
// resolve sonrasında da `status===false` kontrol edilir; `catch` yalnız ağ
// hatası gibi GERÇEKTEN reddeden (mutasyon olmayan bu istekte teorik olarak
// beklenmez ama savunma amaçlı bırakılan) durumlar için ikinci bir katmandır.
export default function GoalCheckInHistory({ goalId, apiPath }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [status, setStatus] = useState('loading'); // loading | ok | hidden
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalRowCount, setTotalRowCount] = useState(0);

  // Hedef değişirse (farklı bir hedef açıldıysa) sayfa 1'e döner.
  useEffect(() => { setPageNumber(1); }, [goalId, apiPath]);

  useEffect(() => {
    if (!goalId || !apiPath) { setStatus('hidden'); return undefined; }
    let cancelled = false;
    setStatus((prev) => (prev === 'ok' ? 'ok' : 'loading'));
    (async () => {
      try {
        const res = await api.get(apiPath, {
          params: { pageNumber, pageSize: PAGE_SIZE },
          skipErrorToast: true,
        });
        if (cancelled) return;
        // Gerçek sinyal `res.data.status` — interceptor 403'te (veya başka
        // bir hatada) `status:false` ile RESOLVE eder (yukarıdaki yorum).
        // `status:true` taşımayan bir yanıt "yasak/hata", "gerçekten boş
        // liste" DEĞİL — bölüm sessizce gizlenir.
        if (!res?.data?.status) { setStatus('hidden'); return; }
        const list = extractListData(res);
        setItems(list);
        setTotalRowCount(res.data?.data?.totalRowCount ?? list.length);
        setStatus('ok');
      } catch {
        // Savunma katmanı — normalde GET'ler burada reddetmez (yukarıdaki
        // yorum), ama beklenmedik bir istisna (ör. ağ katmanı dışı bir throw)
        // olursa da bölüm AYNI şekilde sessizce gizlenir.
        if (!cancelled) setStatus('hidden');
      }
    })();
    return () => { cancelled = true; };
  }, [goalId, apiPath, pageNumber]);

  if (status === 'hidden') return null;

  const totalPages = Math.max(1, Math.ceil(totalRowCount / PAGE_SIZE));

  return (
    <Wrap data-role="goal-check-in-history">
      <SectionTitle>{t('goalCheckIns.sectionTitle')}</SectionTitle>

      {status === 'loading' && <EmptyBox>{t('common.loading')}</EmptyBox>}

      {status === 'ok' && items.length === 0 && (
        <EmptyBox>{t('goalCheckIns.empty')}</EmptyBox>
      )}

      {status === 'ok' && items.map((entry) => (
        <EntryRow key={entry.id} data-role="check-in-entry">
          <EntryHeader>
            <EntryKind>
              {t(`goalCheckIns.valueKind.${entry.valueKind}`, { defaultValue: entry.valueKind })}:
            </EntryKind>
            <EntryChange>
              {formatValue(entry.oldValue, entry.valueKind)} → {formatValue(entry.newValue, entry.valueKind)}
            </EntryChange>
          </EntryHeader>
          <EntryMeta>
            <span>{formatDate(entry.createDate, lang)}</span>
            <span>{t('goalCheckIns.by')}: {entry.createdByName}</span>
          </EntryMeta>
          {entry.note && (
            <EntryNote>{t('goalCheckIns.noteLabel')}: {entry.note}</EntryNote>
          )}
        </EntryRow>
      ))}

      {status === 'ok' && totalPages > 1 && (
        <PagerRow>
          <Button
            variant="outline"
            disabled={pageNumber <= 1}
            onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
          >
            {t('common.previous')}
          </Button>
          <PageLabel>{pageNumber} / {totalPages}</PageLabel>
          <Button
            variant="outline"
            disabled={pageNumber >= totalPages}
            onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
          >
            {t('common.next')}
          </Button>
        </PagerRow>
      )}
    </Wrap>
  );
}
